import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { AxiosError } from 'axios';

import { useMessageHandler } from 'src/utils/hooks/useMessageHandler';
import { getRequest, postRequest } from 'src/utils/request';
import { QUERY_KEYS } from 'src/reactQuery/QueryKeys';
import { API } from 'src/reactQuery/Api';
import { isProd } from 'src/config';

import messages from '../../messages';

interface ProgressResponse {
  state: null | ProgressData;
}

interface ProgressData {
  key: string;
  step: string;
  percent: number;
  started_at: string;
  updated_at: string;
  last_mono: number;
  eta_seconds: number;
  status: 'created' | 'running';
  sub_steps: Record<string, SubStep>;
}

interface SubStep {
  total: number;
  count: number;
  sec_per_sub_step: number;
}

const createPayroll = async ({ date }: { date: string }) => {
  const payload = {
    payroll: {
      selected_date: date,
    },
  };
  await postRequest(API.POST.createPayroll, payload);
};

const getPayrollProgress = async () => {
  const {
    data: { state },
  } = await getRequest<ProgressResponse>(API.GET.getPayrollProgress);
  return state;
};

export const useCreatePayroll = () => {
  const { handleError, handleSuccess } = useMessageHandler();
  const { formatMessage } = useIntl();
  const client = useQueryClient();

  return useMutation({
    mutationFn: createPayroll,
    onSuccess: () => {
      client.invalidateQueries({ queryKey: [QUERY_KEYS.PAYROLLS] });
      handleSuccess(formatMessage(messages.payrollCreated));
    },
    onError: (error: AxiosError) => {
      if (error.code === 'ERR_NETWORK') return;
      handleError(error);
    },
  });
};

export const useGetPayrollProgress = ({
  enabled = false,
}: {
  enabled?: boolean;
}) => {
  return useQuery({
    queryFn: getPayrollProgress,
    queryKey: [QUERY_KEYS.PAYROLL_PROGRESS],
    refetchInterval: (isProd ? 15 : 2) * 1000,
    enabled,
  });
};
